//
// Mental Canvas web viewer.
// (c) 2016-2020 Mental Canvas Inc.
//

import {
    Quaternion,
    Vector3
} from 'three';

import {
    _Math,
    parseBoolean,
    RBT
} from './utils';
import {
    ControlLimits,
} from './MCControls';

// iPadOS13... or, an older iOS with "request desktop site" activated. In both cases,
// the useragent string will look 100% like MacOS.
const bIPadOS13 : boolean = navigator.platform=='MacIntel' && navigator.maxTouchPoints>1;


// This is an alternate way to set up an enum. It has the advantage of being quite succinct, stringy, and
// javascripty. The reason to construct from a keys object is to allow testing of an arbitrary string for
// validity.
const MCQualityKeys = {
    'auto': 0,
    'high': 0,
    'low': 0,
    // Not fully support
    'lower': 0,
    'verylow': 0,
    'lowest': 0
};
export type MCQuality = keyof typeof MCQualityKeys; // i.e. 'auto' | 'high' | 'low' | ...

const MCControlsModeKeys = {
    'classic': 0,
    'disableRotate': 0,
    'authoringNavMode': 0,
    'egocentric': 0
};
export type MCControlsMode = keyof typeof MCControlsModeKeys;

const MCVizModeKeys = {
    // i.e., "canvas visibilities are based on the current scrub time, interpolating between the
    // visibilities of the two temporally adjacent bookmarks"
    'none': 0,
    // i.e., "canvas visibilities are based on the current XYZ position, drawn from the physically
    // closest bookmark's visibilities.
    'positionBased': 0
};
export type MCVizMode = keyof typeof MCVizModeKeys;

export class MCOptions {
    // - path: URL for scene folder, excluding trailing slash.
    // path: string = 'https://mentalcanvas.com/viewer/gallery/acropolis';
    path: string = './RWO';
    // - quality: low/high/auto
    quality: MCQuality = 'low';
    loop: boolean = false;
    maxImageSize: number = 0;
    // controlsMode: MCControlsMode = 'classic';
    controlsMode: MCControlsMode = 'egocentric';
    limits: ControlLimits = null;
    camera: RBT = null;            // A serialized RBT
    groundPlane: string = "";    // A canvas name
    ceilingHeight: number = 0;
    vizMode: MCVizMode = 'positionBased';
    scrub: number = -1;     // elapsed time along bookmark path
    onPath: boolean = true; // is on bookmark path
    fullscreen: boolean = true;

    // Construct from existing object.
    constructor(data: Partial<MCOptions> = {}) {
        // Any unknown values will also be part of the object. (e.g., subclass properties)
        Object.assign(this, data);
    }
    clone(): MCOptions {
        return new MCOptions(this);
    }

    // More careful constructor - as URL options can be anything at all,
    // and can't encode hierarchical objects. Parse and convert types of each option.
    static parseUrl(urlSearch: string): MCOptions
    {
        if (urlSearch[0] == '?')
            urlSearch = urlSearch.substr(1);
        // Convert to a key/value dictionary.
        const urlOptions: {[key: string]: string} =
            urlSearch.split('&').reduce((map: {[key:string]:string}, item) => {
                const equalsPos = item.indexOf('=');
                if (equalsPos > 0)
                    map[item.substr(0, equalsPos)] = decodeURIComponent(item.substr(equalsPos + 1));
                return map;
            }, {});

        var result = new MCOptions;
        if (urlOptions.hasOwnProperty('path'))
            result.path = urlOptions.path;
        if (MCQualityKeys.hasOwnProperty(urlOptions.quality))
            result.quality = <MCQuality>urlOptions.quality;
        if (urlOptions.hasOwnProperty('loop') && parseBoolean(urlOptions.loop) !== undefined)
            result.loop = parseBoolean(urlOptions.loop);
        if (urlOptions.hasOwnProperty('maxImageSize'))
            result.maxImageSize = parseFloat(urlOptions.maxImageSize);
        if (MCControlsModeKeys.hasOwnProperty(urlOptions.controlsMode))
            result.controlsMode = <MCControlsMode>urlOptions.controlsMode;

        var limits = new ControlLimits;
        // launch.html checkboxes marked "off" will *not* transmit the key. Handle that by initializing
        // all to false. Will only actually be used if other limits URL params are present.
        limits.Azimuth = limits.Elevation = limits.Pan = false;
        for (let key of Object.keys(urlOptions))
            if (key.match(/^Limits\./) && limits.parseString(key.replace('Limits.', ''), urlOptions[key]))
                // We found a limits-relevant key in the URL - so we'll attach and use this limits
                // object as part of the options.
                result.limits = limits;

        if (urlOptions.hasOwnProperty('camera')) {
            let cameraCoords:number[] = urlOptions.camera.split(',').map(value => parseFloat(value));
            if (cameraCoords.length == 7)
                result.camera = new RBT(
                    new Quaternion(cameraCoords[3], cameraCoords[4], cameraCoords[5], cameraCoords[6]),
                    new Vector3(cameraCoords[0], cameraCoords[1], cameraCoords[2]));
        }
        if (MCVizModeKeys.hasOwnProperty(urlOptions.vizMode))
            result.vizMode = <MCVizMode>urlOptions.vizMode;
        if (urlOptions.hasOwnProperty('scrub'))
            result.scrub = parseFloat(urlOptions.scrub);
        if (urlOptions.hasOwnProperty('onPath') && parseBoolean(urlOptions.onPath) !== undefined)
            result.onPath = parseBoolean(urlOptions.onPath);
        if (urlOptions.hasOwnProperty('groundPlane'))
            result.groundPlane = urlOptions.groundPlane;
        if (urlOptions.hasOwnProperty('ceilingHeight'))
            result.ceilingHeight = +urlOptions.ceilingHeight;
        
        result.validate();

        /*
        // Collab supports an unusual param style: index.html?foobar&quality=high
        // The "unnamed" param foobar is interpreted as a local scene in the /demo/upload directory, and
        // takes precedence over any path= parameter. It's effectively a type of URL shortening.
        unnamedArg = new RegExp('[\?]([^&#]*)').exec(window.location.search);
        if (unnamedArg && unnamedArg[1].indexOf('=') === -1)
            path = '/demo/upload/' + decodeURI(unnamedArg[1]);*/

        return result;
    }
    private validate()
    {
        // Strip any trailing /Scene.mcx.
        this.path = this.path.replace(/\/Scene.mcx$/, '');

        // Strip any trailing backslashes
        if (this.path != '/')
            this.path = this.path.replace(/\/$/, '');

        // Will be overridden by the scene itself if a collab session is specified.
        switch (this.quality) {
        case 'lowest':
            this.quality = 'low';
            this.maxImageSize = 128;
            break;
        case 'verylow':
            this.quality = 'low';
            this.maxImageSize = 256;
            break;
        case 'low':
            this.quality = 'low';
            this.maxImageSize = 512;
            break;
        case 'auto':
            if (/iPhone|iPad|iPod|Android|\bCrOS\b/i.test(navigator.userAgent)    // iOS, Android, Chromebook
                || bIPadOS13) {    // iPadOS13 - mimics MacOS13 in useragent
                this.quality = 'low';
                console.log('Mobile detected, using low resolution textures');
            }
            else
                this.quality = 'high';
            break;
        }
        if (this.limits && !this.limits.Pan)
            this.limits.PanMax = 10000;
    }

};
